import { Button, IconButton, ViewPlaceholder } from '@vrplatform/components';
import {
  type ApiClient,
  useApiQuery,
  useDashboard,
  useTeamRole,
  useTeamSwitch,
} from '@vrplatform/data';
import { Drawer } from '@vrplatform/components-legacy';
import { showErrorNotification } from '@vrplatform/theme';
import { parseAsString, useQueryState } from '@vrplatform/query-params';
import type { Maybe } from '@vrplatform/utils';
import {
  Avatar,
  Box,
  Group,
  LoadingOverlay,
  Tabs,
  Text,
  rem,
} from '@mantine/core';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ConnectionEllipsisMenuItems } from '../../views/connections/list/connections-table/ConnectionEllipsisMenuItems';
import { ConnectionEllipsisMenuModals } from '../../views/connections/list/connections-table/ConnectionEllipsisMenuModals';
import { DrawerHeader } from '../_components';
import { ConnectionInfoCard, ConnectionSyncsTable } from './_components';
import { useConnectionDetailDrawer } from './useConnectionDetailDrawer';

type Connection = NonNullable<
  NonNullable<ReturnType<typeof useConnectionDetailQuery>['data']>['connection']
>;

export const STORYBOOK_CONNECTION_DETAIL_QUERY_KEY = 'connection-detail-drawer';

function useConnectionDetailQuery(connectionId: Maybe<string>) {
  const { isVrpAdmin } = useTeamRole();

  return useApiQuery(
    async (api, signal, args, $db) => {
      const connectionId = args.connectionId;

      if (!connectionId) return null;

      const tenantId = await $db(
        (q) => {
          return q.connectionById({
            id: connectionId,
          })?.tenantId;
        },
        {
          fetchOptions: {
            signal,
          },
        }
      );

      if (!tenantId) {
        showErrorNotification({
          title: 'Failed to fetch connection',
          message:
            'We were unable to fetch connection. Please reach out to support if the issue persists.',
        });

        throw new Error('Connection not found');
      }

      return await getConnectionData(api, signal, {
        connectionId,
        tenantId,
        isVrpAdmin: args.isVrpAdmin,
      });
    },
    {
      skip: !connectionId,
      variables: {
        connectionId,
        isVrpAdmin,
      },
      queryGroup: 'connections',
      uniqueQueryKey: 'connection-detail-drawer',
      storybookQueryKey: STORYBOOK_CONNECTION_DETAIL_QUERY_KEY,
      hideNotification: true,
    }
  );
}

export async function getConnectionData(
  api: ApiClient,
  signal: AbortSignal,
  {
    connectionId,
    tenantId,
    isVrpAdmin,
  }: {
    connectionId: string;
    tenantId: string;
    isVrpAdmin: boolean;
  }
) {
  const result = await api.GET('/connections/{id}', {
    params: {
      path: {
        id: connectionId,
      },
    },
    // needed when opening from partner dashboard
    headers: {
      'x-tenant-id': tenantId,
    },
    signal,
  });

  if (result.error) {
    if (result.error.code === 'NOT_FOUND')
      return {
        connection: null,
      };
    else throw result.error;
  }

  const connection = result.data;

  const workflows = await api
    .GET('/flows', {
      params: {
        query: {
          appId: result.data.app.id,
        },
      },
      signal,
    })
    .then((result) => {
      const flows = result.data?.data ?? [];

      if (flows.length) {
        const filtered = isVrpAdmin
          ? flows
          : flows.filter((flow) => flow.isPublic);

        return filtered.map((flow) => {
          return {
            ...flow,
            status: connection.enabledFlows.find((f) => f.id === flow.id)
              ? ('active' as const)
              : ('inactive' as const),
            isVrpAdminOnly: !flow.isPublic,
          };
        });
      }

      return [];
    });

  return {
    connection: { ...connection, workflows, tenantId },
  };
}

export const useConnectionConnectModal = () => {
  const [connectionId, setConnectionId] = useQueryState(
    'connectionId',
    parseAsString
  );

  return useMemo(
    () => ({
      reconnect: (id: string) => {
        setConnectionId(id ?? null);
      },
      connectionId,
    }),
    [connectionId, setConnectionId]
  );
};

export const ConnectionDetailDrawer = () => {
  const [dashboard] = useDashboard();

  const setTeamId = useTeamSwitch();
  const goto = useNavigate();

  const { reconnect } = useConnectionConnectModal();

  const { connectionId, close: closeDrawer } = useConnectionDetailDrawer();

  const [opened, setOpened] = useState<{
    connection: Connection;
    modal: 'rename' | 'delete' | 'update' | 'fetchReservation';
  } | null>(null);

  const { data, isLoading, isFetching, error } =
    useConnectionDetailQuery(connectionId);

  const connection = data?.connection;

  const isPartnerDashboard = dashboard === 'partner';

  const openModal = (c: Connection, modal: 'rename' | 'delete' | 'update') => {
    setOpened({ connection: c, modal });
  };
  const closeModal = () => setOpened(null);

  return (
    <>
      <ConnectionEllipsisMenuModals
        data={opened?.connection}
        deleteModal={{
          opened: opened?.modal === 'delete' && !!opened?.connection,
          close: closeModal,
          onSuccess: closeDrawer,
        }}
        updateModal={{
          opened: opened?.modal === 'update' && !!opened?.connection,
          close: closeModal,
        }}
        renameModal={{
          opened: opened?.modal === 'rename' && !!opened?.connection,
          close: closeModal,
        }}
      />

      <Drawer
        opened={!!connectionId}
        onClose={closeDrawer}
        size={800}
        zIndex={100}
      >
        <DrawerHeader
          title={
            connection && (
              <Group mt={rem(5)} wrap="nowrap" flex={1}>
                <Avatar
                  src={connection.app.icon}
                  sx={(theme) => ({
                    border: `1px solid ${theme.colors.gray[2]}`,
                  })}
                />
                <Box>
                  <Text component="p" m={0} color="gray" size="lg">
                    {connection.app.name} /{' '}
                    <Text c="black" fw={500} inherit component="span">
                      {connection.name}
                    </Text>
                  </Text>
                  {connection.uniqueRef && (
                    <Text component="p" m={0} size="sm" color="gray">
                      {connection.uniqueRef}
                    </Text>
                  )}
                </Box>
              </Group>
            )
          }
          loading={isLoading}
          menuItems={
            connection && (
              <ConnectionEllipsisMenuItems
                data={connection}
                openDeleteModal={() => openModal(connection, 'delete')}
                openRenameModal={() => openModal(connection, 'rename')}
                openUpdateModal={() => openModal(connection, 'update')}
              />
            )
          }
          closeDrawer={closeDrawer}
        >
          {['error', 'failed', 'outdated'].includes(connection?.status || '') &&
            connection && (
              <Button
                color="red"
                leftIcon={'LightningIcon'}
                onClick={() => reconnect(connection.id)}
              >
                Reconnect
              </Button>
            )}
          {isPartnerDashboard && connection?.tenantId && (
            <IconButton
              onClick={() => {
                goto(
                  `/connections?search=${encodeURIComponent(
                    connection.name || ''
                  )}`
                );
                setTeamId(connection.tenantId);
              }}
              variant="outline"
              icon="ArrowRightCircleIcon"
              tooltip="Go to team"
            />
          )}
        </DrawerHeader>

        <LoadingOverlay
          visible={isFetching}
          loaderProps={{ size: 'sm' }}
          m="md"
        />
        {error ? (
          <ViewPlaceholder
            title={error?.message || 'Failed to load connection'}
            description="Please try again and contact our customer support if the issue persists."
            icon="AlertTriangleIcon"
            iconColor="orange"
            backgroundColor="transparent"
            contentMaxWidth={300}
          />
        ) : !connection && !isFetching ? (
          <ViewPlaceholder
            title="No connection found"
            description="Has the connection been deleted?"
            icon="CommentQuestionIcon"
            backgroundColor="transparent"
          />
        ) : (
          connection && <Content {...connection} />
        )}
      </Drawer>
    </>
  );
};

const Content = (connection: Connection) => {
  return (
    <>
      <ConnectionInfoCard connection={connection} />

      <Tabs
        keepMounted={false}
        defaultValue={'history'}
        styles={(theme) => ({
          root: {
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
          },
          panel: {
            height: '100%',
            flex: 1,
            marginTop: theme.spacing.lg,
            maxWidth: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
          tab: {
            paddingBlock: theme.spacing.md,
            paddingInline: theme.spacing.sm,
            color: theme.colors.gray[6],
            "&[data-active='true']": {
              color: theme.black,
            },
          },
          tabsList: {
            marginInline: theme.spacing.xs,
          },
        })}
      >
        {/* <Tabs.List>
          <Tabs.Tab value="history">History</Tabs.Tab>
          <Tabs.Tab value="synced-from">Synced from</Tabs.Tab>
          <Tabs.Tab value="synced-to">Synced to</Tabs.Tab>
        </Tabs.List> */}

        <Tabs.Panel value="history">
          <ConnectionSyncsTable
            connectionId={connection.id}
            tenantId={connection.tenantId}
          />
        </Tabs.Panel>
        {/* <Tabs.Panel value="synced-from">
          <ConnectionSyncedFromTable
            connectionId={connectionId}
            tenantId={tenantId}
          />
        </Tabs.Panel> */}
        {/* <Tabs.Panel value="synced-to">
          <ConnectionSyncedToTable
            connectionId={connectionId}
            tenantId={tenantId}
          />
        </Tabs.Panel> */}
      </Tabs>
    </>
  );
};
